import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import CustomModalHeader from '../../../Common/customComponents/CustomModalHeader.tsx';
import { BasicModalV2, TextButton } from '../../../../components';
import CustomText from '../../../Common/customComponents/CustomText.tsx';
import CustomSelectSingle from '../../../Common/customComponents/CustomSelectSingle.tsx';
import SignatureCanvas from 'react-signature-canvas';
import CustomTextarea from '../../../Common/customComponents/CustomTextarea.tsx';
import { useAsync, useUser } from '../../../../hooks';
import { commonMsg } from '../../../../utils/globalUtil.tsx';
import { ptwApi } from '../../../../api/ptw.api.ts';
import FormBtnInnerSearch from '/assets/icon/global/form-btn-inner-search.svg';
import UserSearchModal from '../../Setting/modal/UserSearchModal.tsx';
import ButtonInnerX from '/assets/icon/global/button-inner-x.svg';

interface IProps {
  setIsModalOpen: Dispatch<SetStateAction<boolean>>;
  isModalOpen: boolean;
  title?: string;
  onActionCallback: any;
  approverItem: any;
  isReadonly: boolean;
}

const PtwSignatureModal = (props: IProps) => {
  const { showAlarm, closeAlarm } = useAsync();
  const { userInfo } = useUser();
  const sigRef = useRef<SignatureCanvas | null>(null);
  const [data, setData] = useState<any>({});
  const [nextData, setNextData] = useState<any>({});
  const [isOptional, setIsOptional] = useState<boolean>(false);
  const [selectedUserList, setSelectedUserList] = useState<any[]>([]);
  const [addUserList, setAddUserList] = useState<any[]>([]);

  const [isUserSearchModalOpen, setIsUserSearchModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (props.isModalOpen) {
      setData({
        signType: props.approverItem.signType ? props.approverItem.signType : 'APPROVED',
        sign: props.approverItem.sign,
        comment: props.approverItem.comment
      });
      search();
    } else {
      setData({});
      setAddUserList([]);
      setSelectedUserList([]);
      setNextData({});
      setIsOptional(false);
    }
  }, [props.isModalOpen]);

  const search = async () => {
    const param = {
      approvalMSeq: props.approverItem.approvalMSeq,
      approvalProcessSeq: props.approverItem.approvalProcessSeq
    };
    ptwApi.getPtwNextApprovalItem(param).then(function (r) {
      setNextData(r.resultObject);
    });
  };

  const modalClose = () => {
    props.setIsModalOpen(false);
  };

  const handleChange = (e: any) => {
    setData((oData: any) => {
      return { ...oData, [e.target.name]: e.target.value };
    });
  };

  const ok = () => {
    let dataUrl;
    let base64;
    if (!data.signType) {
      commonMsg.alert(showAlarm, closeAlarm, 'Warning', 'Select a signature type.', null);
      return;
    } else if (data.signType == 'APPROVED') {
      if (!sigRef.current || sigRef.current.isEmpty()) {
        commonMsg.alert(showAlarm, closeAlarm, 'Warning', 'Please sign.', null);
        return;
      } else {
        dataUrl = sigRef.current.getCanvas().toDataURL('image/png');
        base64 = dataUrl.split(',')[1];
      }
    }

    props.onActionCallback(
      props.approverItem.approvalProcessSeq,
      props.approverItem.approvalMSeq,
      props.approverItem.ptwDSeq,
      data.signType,
      base64,
      data.comment,
      addUserList,
      nextData?.approvalMSeq
    );
    props.setIsModalOpen(false);
  };

  const resetSign = () => {
    setData((prev: any) => ({
      ...prev,
      signType: null
    }));

    if (sigRef.current) {
      sigRef.current.clear();
    }
  };

  const resetComment = () => {
    setData((prev: any) => ({
      ...prev,
      comment: ''
    }));
  };

  const onActionUserSearchCallback = (checkedList: any[]) => {
    setAddUserList((prev: any) => [...prev, ...checkedList]);
  };

  const toggleOptional = () => {
    if (isOptional) {
      setIsOptional(false);
      setAddUserList([]);
      setSelectedUserList([]);
    } else {
      setIsOptional(true);
    }
  };

  const delUser = (userId: string) => {
    setAddUserList((prev: any[]) => prev.filter((u) => u.userId !== userId));
  };

  const ActionAssignNextApprover = () => {
    if (!isOptional) {
      commonMsg.alert(showAlarm, closeAlarm, 'Warning', 'Please select optional approvers.', null);
      return;
    }

    setSelectedUserList((prev: any) => [...nextData?.ptwPageApprovalDVOList, ...addUserList]);
    setIsUserSearchModalOpen(true);
  };

  return (
    <BasicModalV2 open={props.isModalOpen} onClose={() => props.setIsModalOpen(false)}>
      <CustomModalHeader title={props.title || 'Signature'} modalClose={modalClose} />
      <div
        className={'custom-modal-body ptw-signature-modal'}
        style={{ width: '500px', height: `${nextData?.approvalMSeq ? '850px' : '550px'}` }}
      >
        <UserSearchModal
          setIsModalOpen={setIsUserSearchModalOpen}
          isModalOpen={isUserSearchModalOpen}
          title={'Approval Authority'}
          onActionCallback={onActionUserSearchCallback}
          selectedList={selectedUserList}
          settingApproverMSeq={nextData?.settingApproverMSeq}
        />
        <div className={'contents-box'}>
          <div className={'row mb-15'}>
            <div className={'col-12 mb-10'}>
              <div className={'top-label-box'}>
                <span>Name</span>
              </div>
              <div>
                <CustomText name={'signNm'} value={userInfo?.fullName} readOnly={true} />
              </div>
            </div>
            <div className={'col-12 mb-15'}>
              <div className={'top-label-box has-btn'}>
                <span>Signature</span>
                {!props.isReadonly && (
                  <TextButton
                    variant="outlined"
                    onClick={() => {
                      resetSign();
                    }}
                  >
                    Reset
                  </TextButton>
                )}
              </div>
              <div className={'mb-10'}>
                <CustomSelectSingle
                  name={'signType'}
                  onChange={handleChange}
                  value={data?.signType}
                  options={[
                    { label: 'APPROVED', value: 'APPROVED' },
                    { label: 'REJECT', value: 'DENY' }
                  ]}
                  readOnly={props.isReadonly}
                />
              </div>

              {data?.signType == 'DENY' && (
                <div className={'sign-deny-div'}>
                  <span className={'sign-deny'}>REJECT</span>
                </div>
              )}

              {!props.isReadonly && (!data?.signType || data?.signType == 'APPROVED') && (
                <div className={'sign-div'}>
                  <SignatureCanvas
                    ref={sigRef}
                    penColor="black"
                    canvasProps={{
                      width: 428,
                      height: 90,
                      style: { width: 428, height: 90, background: '#fff' }
                    }}
                  />
                </div>
              )}
              {props.isReadonly && data?.signType == 'APPROVED' && (
                <div className={'sign-div'}>
                  <img
                    src={`data:image/png;base64,${data.sign}`}
                    alt="signature"
                    style={{ width: '100%', height: '100px' }}
                  />
                </div>
              )}
            </div>
            <div className={'col-12 mb-15'}>
              <div className={'top-label-box has-btn'}>
                <span>Comment</span>
                {!props.isReadonly && (
                  <TextButton
                    variant="outlined"
                    onClick={() => {
                      resetComment();
                    }}
                  >
                    Reset
                  </TextButton>
                )}
              </div>

              <CustomTextarea
                name={'comment'}
                onChange={handleChange}
                value={data?.comment}
                rows={3}
                readOnly={props.isReadonly}
              />
            </div>
            {nextData?.approvalMSeq && (
              <>
                <span className={'next-approval-authority-title'}>APPROVAL AUTHORITY</span>
                <div className={'col-12 mb-15'}>
                  <div className={'top-label-box'}>
                    <span>Current Assign Approvers</span>
                  </div>
                  <div className={'next-approver-box'}>
                    <div className={'approver-select-list'}>
                      {(nextData?.ptwPageApprovalDVOList ?? []).map(
                        (userItem: any, userIndex: number) => (
                          <div className={'selected-item'} key={userIndex}>
                            <span>{userItem.userNm}</span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>

                <div className={'col-12 mb-15'}>
                  <div className={'top-label-box has-btn'}>
                    <span>Additional Approvers</span>
                    <div
                      className={`optional-btn ${isOptional ? 'active' : ''}`}
                      onClick={() => {
                        toggleOptional();
                      }}
                    >
                      Optional
                    </div>
                  </div>
                  <div>
                    <div className={'mb-10'}>
                      <button
                        className={'add-approver-btn'}
                        onClick={() => {
                          ActionAssignNextApprover();
                        }}
                      >
                        <div className={'flex-row gap-1'}>
                          <img src={FormBtnInnerSearch} alt="add-approver" width={20} height={20} />
                          <span>Assign Next Approver</span>
                        </div>
                      </button>
                    </div>
                    <div className={'next-approver-box'}>
                      <div className={'approver-select-list'}>
                        {(addUserList ?? []).map((item: any, index: number) => (
                          <div className={'selected-item'} key={index}>
                            <span>{item.userNm}</span>
                            <button
                              onClick={() => {
                                delUser(item.userId);
                              }}
                            >
                              <img src={ButtonInnerX} alt={'button inner x'} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className={'button-box'}>
            <TextButton
              className={'cancel-style'}
              variant="outlined"
              style={{ height: '40px' }}
              onClick={() => {
                modalClose();
              }}
            >
              Cancel
            </TextButton>

            {!props.isReadonly && (
              <TextButton
                variant="contained"
                style={{ height: '40px' }}
                onClick={() => {
                  ok();
                }}
              >
                OK
              </TextButton>
            )}
          </div>
        </div>
      </div>
    </BasicModalV2>
  );
};

export default PtwSignatureModal;
